import { useQueries } from '@tanstack/react-query';

import type { Loadable, OrderCounts } from './todo';

import type { MerchantOrderPage } from '@/features/orders/types';
import { ApiError, apiRequest } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * The three order counts « À traiter » shows, each a real `total` from `GET /merchant/orders?status=…&limit=1` (one
 * row is fetched, only the total is used): accepted (to prepare), being prepared (to pack), packed (to ship).
 */
const STATUS_BY_COUNT = {
  toPrepare: 'PAYMENT_CONFIRMED',
  toPack: 'PROCESSING',
  toShip: 'PACKED',
} as const;

type CountKey = keyof typeof STATUS_BY_COUNT;

const COUNT_KEYS = Object.keys(STATUS_BY_COUNT) as readonly CountKey[];

export function useOrderCountsQuery(): Loadable<OrderCounts> {
  const { accessToken, activeShopId } = useSession();

  const queries = useQueries({
    queries: COUNT_KEYS.map((key) => ({
      queryKey: ['orders', activeShopId, STATUS_BY_COUNT[key], 'count'],
      queryFn: () =>
        apiRequest<MerchantOrderPage>(`/merchant/orders?status=${STATUS_BY_COUNT[key]}&limit=1`, {
          accessToken,
          shopId: activeShopId,
        }),
      enabled: accessToken !== null && activeShopId !== null,
    })),
  });

  if (queries.some((query) => query.error instanceof ApiError && query.error.status === 403)) {
    return { state: 'forbidden' };
  }
  if (queries.some((query) => query.isError)) return { state: 'error' };
  if (queries.some((query) => query.isPending)) return { state: 'pending' };

  const totals: Record<CountKey, number> = { toPrepare: 0, toPack: 0, toShip: 0 };
  COUNT_KEYS.forEach((key, index) => {
    totals[key] = queries[index]?.data?.total ?? 0;
  });
  return { state: 'ready', data: totals };
}
